// Work item reference — the card's arithmetic, kept free of React and of any
// import so the node suite can prove it (run-work-item-ref-tests.mjs).
//
//   refKeyOf(value)        the key a field value names, or why it names none
//   statusClassFor(cat)    the lozenge class for a status category key
//   progressOf(progress)   "N of M done" and the bar's width; null for no family
//   dueInfo(day, opts)     how a Due date reads: late or not, "Due in N days"
//   treeIndents(n, opts)   where each row of the card's tree sits (project,
//                          the work items above, the referenced one)

// Same shape the server route accepts (work-item-ref-handler.js KEY_RE).
const KEY_RE = /^[A-Z][A-Z0-9_]{0,29}-\d{1,9}$/;

// The field's value is a plain string (one key), trimmed and read in upper
// case the way the server matches it. Returns { text, key }: text is what is
// stored (to show when it names nothing), key the key to look up or null.
export function refKeyOf(value) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const upper = text.toUpperCase();
  return { text, key: KEY_RE.test(upper) ? upper : null };
}

// The panel's lozenge classes (status-badge status-…), from the category key
// the route sends ('new' | 'indeterminate' | 'done').
export function statusClassFor(categoryKey) {
  if (categoryKey === 'done') return 'done';
  if (categoryKey === 'indeterminate') return 'in-progress';
  return 'todo';
}

// A level's progress as the card draws it — the referenced work item's family,
// or a work item above it with everything under it. Nothing to count (M = 0)
// draws nothing.
export function progressOf(progress) {
  const total = Math.max(0, Math.floor(Number(progress?.total) || 0));
  if (!total) return null;
  const done = Math.min(total, Math.max(0, Math.floor(Number(progress?.done) || 0)));
  return { done, total, pct: Math.round((done / total) * 100), label: `${done} of ${total} done` };
}

const DAY_MS = 24 * 60 * 60 * 1000;
// A calendar day as a LOCAL midnight, so the count never shifts by a day with
// the viewer's timezone (new Date('2026-10-20') would be UTC midnight).
function localDay(text) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(text ?? ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

// How a work item level's Due date reads. late: past and not Done (the
// card paints the date red). relative: "Due today", "Due in 3 days",
// "2 days late" — only while the work is open; a Done item's date is history.
// Days are counted between local midnights and rounded, so a daylight-saving
// change in between does not make 23 hours "0 days".
export function dueInfo(duedate, { done = false, today = new Date() } = {}) {
  const due = localDay(duedate);
  if (!due) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due - start) / DAY_MS);
  const late = !done && days < 0;
  let relative = null;
  if (!done) {
    if (days === 0) relative = 'Due today';
    else if (days > 0) relative = `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
    else relative = `${-days} ${days === -1 ? 'day' : 'days'} late`;
  }
  return { days, late, relative };
}

// The card's tree, in px. Rows top to bottom: the project (when shown), then
// levelCount work item levels (the work items above, then the referenced one).
// Each row steps in TREE_STEP from the row above — up to TREE_MAX_STEPS, so a
// deep chain never squeezes the referenced work item in a ~380 px column —
// and its line comes down from the row above's icon: at = its own indent,
// from = the row above's (null for the card's first row: no line into it).
export const TREE_STEP = 18;
export const TREE_MAX_STEPS = 4;
export function treeIndents(levelCount, { withProject = false } = {}) {
  const indentOf = (depth) => Math.min(depth, TREE_MAX_STEPS) * TREE_STEP;
  const first = withProject ? 1 : 0;
  const levels = [];
  for (let i = 0; i < Math.max(0, levelCount); i++) {
    const depth = i + first;
    levels.push({ at: indentOf(depth), from: depth === 0 ? null : indentOf(depth - 1) });
  }
  return { project: withProject ? { at: 0, from: null } : null, levels };
}
